import type { LatestNewsModule } from 'types/capi/modules';
import type {
  Image as ImageType,
  InternalLink as CapiInternalLink
} from 'types/capi/shared';

import ChevronIcon from '~/assets/icons/Chevron';
import Image from '~/components/Image';
import Tracked from '~/components/Tracked';
import useEnv from '~/hooks/useEnv';

function CarouselItem({
  category,
  image,
  link
}: {
  category: string;
  image: ImageType;
  link: CapiInternalLink;
}) {
  return (
    <Tracked.InternalLink
      to={link.path}
      variant="none"
      pagePosition="Homepage Latest News"
      name="LATEST_NEWS"
    >
      <div className="flex flex-col">
        <div className="flex flex-col gap-2 rounded bg-white text-start max-md:w-[320px]">
          <Image
            src={image.url}
            className="w-full"
            alt={image.alt}
            quality={100}
            loading="lazy"
            responsiveOptions={{
              md: { width: 900 },
              xs: { width: 672 }
            }}
          />
          <h4 className="text-start text-xs text-grey-hide">{category}</h4>
          <h3 className="text-sm tracking-wide text-black">{link.text}</h3>
          <div className="flex flex-row items-center gap-2 text-nowrap text-sm text-grey-hide">
            <p> View article </p>
            <div className="flex rotate-270 justify-center">
              <ChevronIcon stroke="#A9A9A9" width="8px" height="8px" />
            </div>
          </div>
        </div>
      </div>
    </Tracked.InternalLink>
  );
}

function ViewAllNewsButton() {
  const env = useEnv();

  return (
    <div className="w-36">
      <Tracked.ExternalLink
        variant="transparent-black-border"
        className="flex items-center justify-center !font-bold"
        pagePosition="Homepage Latest News"
        name="VIEW_ALL"
        href={`${env.SITE_BASE_URL}/news/`}
      >
        View all news
      </Tracked.ExternalLink>
    </div>
  );
}

type CarouselProps = {
  items: LatestNewsModule['items'];
};

function Carousel({ items }: CarouselProps) {
  return (
    <div className="no-scrollbar flex items-start gap-2 self-stretch overflow-x-scroll max-md:-mx-4 max-md:px-4 md:gap-6">
      {items.map((i) => (
        <CarouselItem
          image={i.image}
          link={i.link}
          key={i.link.path}
          category={i.category}
        />
      ))}
    </div>
  );
}

type Props = { items: LatestNewsModule['items']; title: string };

export default function ArticleCarousel({ items, title }: Props) {
  return (
    <div className="w-full bg-white md:px-11">
      <div className="mx-auto flex max-w-desktop flex-col items-center justify-center gap-6 py-10 max-md:px-4 md:py-20">
        <div className="flex flex-col justify-center gap-8 self-stretch">
          <h2 className="text-center text-[32px] leading-10 md:text-[40px] md:leading-[48px]">
            {title}
          </h2>
          <div className="flex flex-col items-center gap-6 md:gap-8">
            <Carousel items={items} />
            <ViewAllNewsButton />
          </div>
        </div>
      </div>
    </div>
  );
}
